import { findAnchor, type TourAnchor } from './anchors';

/** Long enough for a cold hub to finish its first paint, short enough to give up. */
const TIMEOUT_MS = 8_000;

/**
 * Run `callback` once an anchor is on the page and has been laid out.
 *
 * A hub mounts before its queries resolve, so the first step's element can be a
 * frame or a fetch away. Waiting for a measurable box — not merely for the node
 * — is what keeps the very first highlight from being drawn around a collapsed
 * element and then jumping.
 *
 * @returns a cancel function; call it when the screen unmounts.
 */
export function waitForAnchor(anchor: TourAnchor, callback: () => void): () => void {
    if (typeof window === 'undefined') return () => { };

    let frame = 0;
    let cancelled = false;
    const deadline = Date.now() + TIMEOUT_MS;

    const poll = () => {
        if (cancelled) return;

        const element = findAnchor(anchor);
        if (element) {
            const { width, height } = element.getBoundingClientRect();
            if (width > 0 && height > 0) {
                callback();
                return;
            }
        }

        if (Date.now() > deadline) return;
        frame = window.requestAnimationFrame(poll);
    };

    frame = window.requestAnimationFrame(poll);

    return () => {
        cancelled = true;
        window.cancelAnimationFrame(frame);
    };
}
